import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Stack,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  Chip,
  Card,
  CardContent,
} from "@mui/material";
import {
  Search as SearchIcon,
  FileDownload as FileDownloadIcon,
  FilterList as FilterListIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import * as XLSX from "xlsx";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function EnhancedTableHead({ columns, order, orderBy, onRequestSort }) {
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {columns.map((column) => (
          <TableCell
            key={column.field}
            align={column.numeric ? "right" : "left"}
            padding={column.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === column.field ? order : false}
            sx={{
              fontWeight: "bold",
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? theme.palette.background.paper
                  : theme.palette.grey[100],
              whiteSpace: "nowrap",
            }}
          >
            <TableSortLabel
              active={orderBy === column.field}
              direction={orderBy === column.field ? order : "asc"}
              onClick={createSortHandler(column.field)}
            >
              {column.headerName || column.field}
            </TableSortLabel>
          </TableCell>
        ))}
        <TableCell
          align="center"
          sx={{
            fontWeight: "bold",
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? theme.palette.background.paper
                : theme.palette.grey[100],
          }}
        >
          Actions
        </TableCell>
      </TableRow>
    </TableHead>
  );
}

export default function EnhancedTable({
  columns,
  data,
  loading,
  onEdit,
  onDelete,
  onRowClick,
  title,
  showActions = true,
  customActions,
  filters = [],
  uniqueId = "id",
}) {
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState(columns[0]?.field || "id");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  // Update filtered data when source data or filters change
  useEffect(() => {
    if (!data) return setFilteredData([]);

    let filtered = [...data];

    // Apply search filter across all columns
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((row) =>
        columns.some((column) => {
          const value = row[column.field];
          return value && String(value).toLowerCase().includes(searchLower);
        })
      );
    }

    // Apply active filters
    Object.entries(activeFilters).forEach(([field, value]) => {
      if (value) {
        filtered = filtered.filter((row) => {
          const rowValue = row[field];
          return (
            rowValue &&
            String(rowValue).toLowerCase() === String(value).toLowerCase()
          );
        });
      }
    });

    setFilteredData(filtered);
  }, [data, searchTerm, activeFilters, columns]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleExportExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
    XLSX.writeFile(
      workbook,
      `${title || "Export"}_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  const handleFilterClick = (field, value) => {
    setActiveFilters((prev) => ({
      ...prev,
      [field]: prev[field] === value ? null : value,
    }));
    setPage(0);
  };

  const handleFilterClear = () => {
    setActiveFilters({});
    setPage(0);
  };

  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleEdit = () => {
    if (onEdit && selectedRow) {
      onEdit(selectedRow);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (onDelete && selectedRow) {
      onDelete(selectedRow);
    }
    handleMenuClose();
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredData.length) : 0;

  const visibleRows = stableSort(
    filteredData,
    getComparator(order, orderBy)
  ).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ width: "100%" }}>
      <Card elevation={4} sx={{ mb: 2 }}>
        <CardContent>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={2}
            mb={2}
          >
            {title && (
              <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                {title}
              </Typography>
            )}

            <TextField
              variant="outlined"
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="clear search"
                      onClick={() => setSearchTerm("")}
                      edge="end"
                      size="small"
                    >
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              size="small"
              sx={{ width: { xs: "100%", sm: "220px" } }}
            />

            <Stack direction="row" spacing={1}>
              {filteredData.length > 0 && (
                <Tooltip title="Export to Excel">
                  <Button
                    variant="outlined"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleExportExcel}
                    size="small"
                  >
                    Export
                  </Button>
                </Tooltip>
              )}

              {customActions}
            </Stack>
          </Stack>

          {filters.length > 0 && (
            <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
              {filters.map((filter) => (
                <Box key={filter.field} sx={{ mr: 2, mb: 1 }}>
                  <Typography variant="caption" display="block" gutterBottom>
                    {filter.name}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {filter.options.map((option) => (
                      <Chip
                        key={option.value}
                        label={option.label}
                        onClick={() =>
                          handleFilterClick(filter.field, option.value)
                        }
                        color={
                          activeFilters[filter.field] === option.value
                            ? "primary"
                            : "default"
                        }
                        size="small"
                        variant={
                          activeFilters[filter.field] === option.value
                            ? "filled"
                            : "outlined"
                        }
                      />
                    ))}
                  </Box>
                </Box>
              ))}

              {Object.keys(activeFilters).some((key) => activeFilters[key]) && (
                <Button
                  variant="text"
                  size="small"
                  onClick={handleFilterClear}
                  startIcon={<CloseIcon />}
                  sx={{ ml: "auto", alignSelf: "flex-end" }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          )}

          <TableContainer component={Paper} elevation={0}>
            <Table
              sx={{ minWidth: 750 }}
              aria-labelledby="tableTitle"
              size="medium"
            >
              <EnhancedTableHead
                columns={columns}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + 1}
                      align="center"
                      sx={{ py: 3 }}
                    >
                      <Typography>Loading...</Typography>
                    </TableCell>
                  </TableRow>
                ) : visibleRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + 1}
                      align="center"
                      sx={{ py: 3 }}
                    >
                      <Typography>No records found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleRows.map((row, index) => (
                    <TableRow
                      hover
                      onClick={() => onRowClick && onRowClick(row)}
                      key={row[uniqueId] || index}
                      sx={{
                        cursor: onRowClick ? "pointer" : "default",
                        "&:nth-of-type(odd)": {
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark"
                              ? theme.palette.action.hover
                              : theme.palette.grey[50],
                        },
                      }}
                    >
                      {columns.map((column) => (
                        <TableCell
                          key={`${row[uniqueId]}-${column.field}`}
                          align={column.align || "left"}
                        >
                          {column.renderCell
                            ? column.renderCell({ row })
                            : row[column.field]}
                        </TableCell>
                      ))}

                      {showActions && (
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleMenuOpen(event, row);
                            }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={columns.length + 1} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {onEdit && (
          <MenuItem onClick={handleEdit}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit
          </MenuItem>
        )}
        {onDelete && (
          <MenuItem onClick={handleDelete}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}
